import { type SyntheticEvent, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Grow, Paper, Popper } from "@mui/material";
import useBooleanState from "src/hooks/useBooleanState";
import { useClickOutside } from "src/hooks/useClickOutside";
import { DROPDOWN_SURFACE } from "src/theme/dropdownSurface";
import {
  DEFAULT_OFFSET,
  DEFAULT_PLACEMENT,
  GROW_STYLE,
  GROW_TIMEOUT,
} from "./PopperDropdown.constants";
import type { PopperDropdownProps } from "./PopperDropdown.types";

const PopperDropdown = ({
  children,
  content,
  placement = DEFAULT_PLACEMENT,
  offset = DEFAULT_OFFSET,
  slotProps,
}: PopperDropdownProps) => {
  const { visible: open, hide, toggle } = useBooleanState();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleOnMouseDownCapture = useClickOutside(hide);
  useHotkeys("esc", hide, { enabled: open });

  const setAnchorRef = (el: HTMLElement | null) => {
    setAnchorEl(el);
  };

  const toggleAndPrevent = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  const resolvedContent = open
    ? typeof content === "function"
      ? content({ hide })
      : content
    : null;

  const { sx: paperSx, ...paperProps } = slotProps?.paper ?? {};

  return (
    <>
      {children({
        isOpen: open,
        toggle,
        toggleAndPrevent,
        hide,
        anchorRef: setAnchorRef,
        handleOnMouseDownCapture,
      })}
      <Popper
        open={open && !!anchorEl}
        anchorEl={anchorEl}
        // useClickOutside uses React tree; portaled popper would not read as "inside" the anchor
        transition
        placement={placement}
        modifiers={[{ name: "offset", options: { offset } }]}
        {...slotProps?.popper}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={GROW_STYLE} timeout={GROW_TIMEOUT}>
            <Paper
              {...paperProps}
              sx={[
                DROPDOWN_SURFACE,
                ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
              ].filter(Boolean)}
              onMouseDownCapture={handleOnMouseDownCapture}
              onKeyDownCapture={(e) => {
                if (e.key === "Escape") hide();
              }}
            >
              {resolvedContent}
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default PopperDropdown;
